import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history } from '@umijs/max';
import { message } from 'antd';
import { TOAST_MESSAGE_CONFIG } from '@/shared/utils/toast';
import React from 'react';

message.config(TOAST_MESSAGE_CONFIG);

import {
  AvatarDropdown,
  AvatarName,
  Logo,
  ThemeRoot,
  UserAvatar,
} from '@/components';
import { THEME_STORAGE_KEY } from '@/components/ThemeRoot';
import { getCurrentUser } from '@/services/auth/api';
import {
  clearTokens,
  getUserInfo,
  isLoggedIn,
} from '@/services/auth/tokenManager';
import { installAutofillDisabler } from '@/utils/disableAutofill';
import { trackPageView } from '@/utils/track';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';

installAutofillDisabler();

const loginPath = '/user/login';

const AUTH_BASE_URL = process.env.Auth_URL || '';

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 */

function readInitialThemeMode(): 'light' | 'dark' {
  if (typeof window === 'undefined') return 'dark';
  const v = localStorage.getItem(THEME_STORAGE_KEY);
  if (v === 'light' || v === 'dark') return v;
  if (window.matchMedia?.('(prefers-color-scheme: dark)').matches)
    return 'dark';
  return 'light';
}

export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;

  themeMode?: 'light' | 'dark';
}> {
  const fetchUserInfo = async () => {
    if (!isLoggedIn()) {
      return undefined;
    }

    try {
      const response = await getCurrentUser();
      if (response.success && response.data) {
        const user = response.data;
        return {
          name: user.profile?.nickname || user.email || '',
          avatar: user.profile?.avatar || '',
          userid: user.userId || user.userID,
          userId: user.userId || user.userID,
          _id: user._id,
          nickname: user.profile?.nickname,
          email: user.email,
          access: user.roles?.includes('admin') ? 'admin' : 'user',
          roles: user.roles || [],
        } as API.CurrentUser;
      }
    } catch (_error) {}

    const cachedUser = getUserInfo();
    if (cachedUser) {
      return {
        name: cachedUser.profile?.nickname || cachedUser.email || '',
        avatar: cachedUser.profile?.avatar || '',
        userid: cachedUser.userId || cachedUser.userID,
        userId: cachedUser.userId || cachedUser.userID,
        _id: cachedUser._id,
        nickname: cachedUser.profile?.nickname,
        email: cachedUser.email,
        access: cachedUser.roles?.includes('admin') ? 'admin' : 'user',
        roles: cachedUser.roles || [],
      } as API.CurrentUser;
    }

    clearTokens();
    return undefined;
  };

  const { location } = history;
  if (location.pathname !== loginPath) {
    const currentUser = await fetchUserInfo();
    if (!currentUser) {
      history.push(loginPath);
    }
    return {
      fetchUserInfo,
      currentUser,
      settings: defaultSettings as Partial<LayoutSettings>,
      themeMode: readInitialThemeMode(),
    };
  }

  return {
    fetchUserInfo,
    settings: defaultSettings as Partial<LayoutSettings>,
    themeMode: readInitialThemeMode(),
  };
}

export const layout: RunTimeLayoutConfig = ({ initialState }) => {
  const isDark = initialState?.themeMode === 'dark';
  return {
    navTheme: isDark ? 'realDark' : 'light',

    token: {
      sider: {
        colorBgMenuItemSelected: isDark
          ? 'rgba(255, 255, 255, 0.10)'
          : 'rgba(0, 0, 0, 0.06)',
        colorBgMenuItemHover: isDark
          ? 'rgba(255, 255, 255, 0.06)'
          : 'rgba(0, 0, 0, 0.04)',
        colorTextMenuSelected: isDark ? '#fafafa' : '#171717',
        colorTextMenuItemHover: isDark ? '#fafafa' : '#171717',
        colorTextMenuActive: isDark ? '#fafafa' : '#171717',
      },
    },

    menu: { defaultOpenAll: true },

    collapsedButtonRender: false,

    breadcrumbRender: false,

    actionsRender: () => [],

    avatarProps: {
      render: () => {
        const u = initialState?.currentUser;
        return (
          <AvatarDropdown>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '4px 8px',
                cursor: 'pointer',
                borderRadius: 'var(--ant-border-radius)',
              }}
            >
              <UserAvatar
                src={u?.avatar}
                name={u?.name}
                email={u?.email}
                size={32}
              />
              <AvatarName />
            </span>
          </AvatarDropdown>
        );
      },
    },

    waterMarkProps: {
      content: initialState?.currentUser?.name,
      gap: [200, 200],
      rotate: -18,
      font: {
        fontSize: 14,
        color: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
      },
    },
    footerRender: false,
    onPageChange: () => {
      const { location } = history;
      trackPageView(location.pathname);

      if (!initialState?.currentUser && location.pathname !== loginPath) {
        history.push(loginPath);
        return;
      }

      if (location.pathname === '/') {
        history.push('/users-center/users');
      }
    },
    links: [],
    menuHeaderRender: (_logo, _title, props) => (
      <Logo height={40} />
    ),
    ...initialState?.settings,
  };
};

export const request: RequestConfig = {
  baseURL: AUTH_BASE_URL,
  ...errorConfig,
};

export function innerProvider(container: React.ReactNode): React.ReactNode {
  return <ThemeRoot>{container}</ThemeRoot>;
}
